import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge"
import {
  isBoundField,
  type BindingKey,
  type FormControlKey,
  type ModuleDefinitionField,
} from "shared"
import type { ModuleEvent } from "../stores/moduleState"
import type { DragData } from "../../../dragAndDrop/dragData"
import { dragKind } from "../../../dragAndDrop/dragKind"
import { dropZone } from "../../../dragAndDrop/dropZone"
import { offCanvas } from "../../../dragAndDrop/offCanvas"
import { moveForDrop, placeForDrop } from "../../../dragAndDrop/reorder"
import { newBoundField, newField } from "./moduleFields"

/** A field on the canvas, by its id, as it is dragged. */
export const placedField = dragKind<string>("placed field")

/** A control in the library, by its key, as it is dragged onto the canvas. */
export const libraryControl = dragKind<FormControlKey>("library control")

/** A session field in the library, by its binding, as it is dragged onto the canvas. */
export const libraryBinding = dragKind<BindingKey>("library binding")

/** A field's card on the canvas, by the field's id, as a place to drop on. */
export const placedFieldZone = dropZone<string>("placed field")

/** The canvas, which keeps a field let go of between or below the others. */
export const moduleCanvas = dropZone("module canvas")

/** Whether a drag is a field on the canvas, which the page takes off. */
export const isPlacedField = (source: DragData) =>
  placedField.from(source) !== undefined

/** Whether a drag is a control from the library, which the canvas adds. */
export const isLibraryControl = (source: DragData) =>
  libraryControl.from(source) !== undefined

/** Whether a drag is anything from the library, which the canvas adds. */
export const isFromLibrary = (source: DragData) =>
  isLibraryControl(source) || libraryBinding.from(source) !== undefined

/** Whether a drag is a field, placed or to add: what the canvas and its cards take. */
export const isFieldDrag = (source: DragData) =>
  isPlacedField(source) || isFromLibrary(source)

/**
 * The event a drop makes on the module, as its buttons would make it, or null
 * for a drop no rule covers. Each rule names both what was dragged and where
 * it landed, so a place that starts taking a new kind of drag changes nothing
 * until it has a rule here.
 */
export function eventForDrop(
  fields: ModuleDefinitionField[],
  source: DragData,
  target: DragData | undefined,
): ModuleEvent | null {
  if (!target) return null
  // A session field goes on a module once, as its button is held back.
  const binding = libraryBinding.from(source)
  if (binding && fields.some((f) => isBoundField(f) && f.binding === binding)) {
    return null
  }
  const adding = newFieldFor(source)
  if (adding) {
    if (moduleCanvas.is(target)) {
      return { type: "added", field: adding, at: fields.length }
    }
    const onto = placedFieldZone.from(target)
    return onto === undefined
      ? null
      : {
          type: "added",
          field: adding,
          at: placeForDrop(fields, onto, extractClosestEdge(target)),
        }
  }
  const id = placedField.from(source)
  if (id === undefined) return null
  if (offCanvas.is(target)) return { type: "removed", id }
  const over = placedFieldZone.from(target)
  if (over === undefined) return null
  const move = moveForDrop(fields, id, over, extractClosestEdge(target))
  return move && { type: "moved", ...move }
}

/** The new field a drag from the library would add, if it is from there. */
function newFieldFor(source: DragData): ModuleDefinitionField | undefined {
  const control = libraryControl.from(source)
  if (control) return newField(control)
  const binding = libraryBinding.from(source)
  return binding && newBoundField(binding)
}
